import { useCallback, useEffect, useState } from "react";

import { v4 as uuidv4 } from "uuid";
import { doc, onSnapshot } from "firebase/firestore";

import { db, useAccountData, uploadFileToStorage } from "./firebase";
import { fetchOurApi } from "./utils/apiCalls";

const BUCKET = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;

const uploadOneBomFile = async ({ companyId, batchId, file, formatKey }) => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${companyId}/bom-uploads/${batchId}/${safeName}`;
  await uploadFileToStorage({
    path,
    fileObject: file,
    hasUserConfirmedUpload: true
  });
  return { fileName: file.name, gcsPath: path, formatKey: formatKey || null };
};

export const startBomBulkUploadBatch = async ({
  companyId,
  files,
  formatKeysByFileName = {},
  confirmedExtractions = {}
}) => {
  if (!files?.length) {
    return { error: "no-files" };
  }
  const batchId = uuidv4();
  const uploaded = await Promise.all(
    files.map((file) =>
      uploadOneBomFile({
        companyId,
        batchId,
        file,
        formatKey: formatKeysByFileName[file.name] || null
      })
    )
  );

  const result = await fetchOurApi({
    accountId: companyId,
    path: "/products/start-bom-bulk-upload",
    method: "POST",
    data: {
      companyId,
      batchId,
      bucket: BUCKET,
      files: uploaded,
      confirmedExtractions
    }
  });

  if (!result?.taskDocId) {
    return { error: result?.message || "task-not-created" };
  }
  return { taskDocId: result.taskDocId, batchId };
};

export const subscribeToBomBulkUploadTask = ({
  companyId,
  taskDocId,
  onUpdate
}) => {
  const ref = doc(db, "v3-companies", companyId, "tasks", taskDocId);
  return onSnapshot(ref, (snapshot) => {
    if (!snapshot.exists()) {
      return;
    }
    onUpdate(snapshot.data());
  });
};

export const useBomTemplates = () => {
  const [{ id: accountId }] = useAccountData();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplates = useCallback(async () => {
    if (!accountId) {
      return;
    }
    const result = await fetchOurApi({
      accountId,
      path: "/products/list-bom-templates",
      method: "POST",
      data: {}
    }).catch((err) => ({ __error: err?.message || "fetch-failed" }));
    if (result?.__error) {
      setError(result.__error);
    } else {
      setError(null);
      setTemplates(result?.templates || []);
    }
    setIsLoading(false);
  }, [accountId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const saveTemplate = async (template) => {
    if (!accountId || !template?.formatKey) {
      return { success: false, error: "missing-account-or-formatKey" };
    }
    const id = template.id || `tmpl-${template.formatKey}`;
    const payload = { ...template, id };
    const result = await fetchOurApi({
      accountId,
      path: "/products/save-bom-template",
      method: "POST",
      data: { template: payload }
    }).catch((err) => ({ success: false, error: err?.message || "save-failed" }));
    if (result?.success) {
      setTemplates((current) => [
        ...current.filter((t) => t.id !== id),
        payload
      ]);
    }
    return result || { success: false, error: "save-failed" };
  };

  const deleteTemplate = async (id) => {
    if (!accountId || !id) {
      return { success: false, error: "missing-account-or-id" };
    }
    const result = await fetchOurApi({
      accountId,
      path: "/products/delete-bom-template",
      method: "POST",
      data: { id }
    }).catch((err) => ({ success: false, error: err?.message || "delete-failed" }));
    if (result?.success) {
      setTemplates((current) => current.filter((t) => t.id !== id));
    }
    return result || { success: false, error: "delete-failed" };
  };

  const clearAllTemplates = async () => {
    const ids = templates.map(({ id }) => id);
    const results = await Promise.all(ids.map((id) => deleteTemplate(id)));
    const failed = results.filter((r) => !r?.success);
    return {
      success: failed.length === 0,
      failedCount: failed.length,
      ...(failed.length ? { error: failed[0]?.error || "clear-failed" } : {})
    };
  };

  return [
    templates,
    isLoading,
    { saveTemplate, deleteTemplate, clearAllTemplates, error, refetch: fetchTemplates }
  ];
};
